import type { CustomerFavouritesOwner } from "./domain/customerFavouritesContracts";
import {
  CUSTOMER_FAVOURITES_FIXTURE_PROVIDER_ID,
  CUSTOMER_FAVOURITES_FIXTURE_PROVIDER_REVISION,
} from "./domain/customerFavouritesFixtures";

const IDENTIFIER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/u;
export const CUSTOMER_FAVOURITE_OPERATION_ID_PATTERN =
  /^customer-favourite-operation-[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;

export type CustomerFavouriteActionIntent = "add" | "remove";

export interface CustomerFavouriteActionRequest {
  operationId: string;
  owner: CustomerFavouritesOwner;
  locationId: string;
  intent: CustomerFavouriteActionIntent;
}

interface CustomerFavouriteActionResultIdentity {
  operationId: string;
  customerAccountId: string;
  providerId: string;
  providerRevision: string;
  snapshotId: string;
  snapshotRevision: string;
  displayRevision: string;
  locationId: string;
  intent: CustomerFavouriteActionIntent;
}

export type CustomerFavouriteActionResult =
  | (CustomerFavouriteActionResultIdentity & {
      status: "preview-authorized";
      evidence: "frontend-fixture-preview";
      backendSynchronization: "not-claimed";
    })
  | (CustomerFavouriteActionResultIdentity & {
      status: "unsupported";
      reason: "provider-disabled";
    })
  | (CustomerFavouriteActionResultIdentity &
      (
        | {
            status: "failed";
            reason: "invalid-request";
            retryable: false;
          }
        | {
            status: "failed";
            reason: "provider-request-failed";
            retryable: true;
          }
      ))
  | (CustomerFavouriteActionResultIdentity & {
      status: "unknown";
      reason: "provider-outcome-unknown";
    })
  | (CustomerFavouriteActionResultIdentity & {
      status: "auth-expired";
      reason: "customer-session-expired";
    })
  | (CustomerFavouriteActionResultIdentity & {
      status: "customer-mismatch";
      expectedCustomerAccountId: string;
      receivedCustomerAccountId: string;
    });

export interface CustomerFavouriteActionProvider {
  readonly providerId: string;
  readonly providerRevision: string;
  mutate(
    request: CustomerFavouriteActionRequest,
    signal?: AbortSignal,
  ): Promise<CustomerFavouriteActionResult>;
}

export const CUSTOMER_FAVOURITE_ACTION_FIXTURE_SCENARIOS = [
  "preview-authorized",
  "add-failed",
  "remove-failed",
  "unknown",
  "unsupported",
  "auth-expired",
  "customer-mismatch",
] as const;

export type CustomerFavouriteActionFixtureScenario =
  (typeof CUSTOMER_FAVOURITE_ACTION_FIXTURE_SCENARIOS)[number];

function isIdentifier(value: unknown): value is string {
  return typeof value === "string" && IDENTIFIER_PATTERN.test(value);
}

function formatUuidV4(bytes: Uint8Array) {
  const hexadecimal = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

  return [
    hexadecimal.slice(0, 8),
    hexadecimal.slice(8, 12),
    hexadecimal.slice(12, 16),
    hexadecimal.slice(16, 20),
    hexadecimal.slice(20),
  ].join("-");
}

/**
 * Creates a per-attempt opaque operation ID without relying on tab-local
 * counters. Returning null is deliberate: an action must not be sent when a
 * cryptographically random nonce cannot be produced.
 */
export function createCustomerFavouriteOperationId() {
  const cryptoRuntime = globalThis.crypto;

  try {
    const uuid =
      typeof cryptoRuntime?.randomUUID === "function"
        ? cryptoRuntime.randomUUID().toLowerCase()
        : (() => {
            if (typeof cryptoRuntime?.getRandomValues !== "function") {
              return null;
            }

            const bytes = cryptoRuntime.getRandomValues(new Uint8Array(16));
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            return formatUuidV4(bytes);
          })();

    const operationId = uuid
      ? `customer-favourite-operation-${uuid}`
      : null;
    return operationId &&
      CUSTOMER_FAVOURITE_OPERATION_ID_PATTERN.test(operationId)
      ? operationId
      : null;
  } catch {
    return null;
  }
}

function isOwner(owner: unknown): owner is CustomerFavouritesOwner {
  if (!owner || typeof owner !== "object" || Array.isArray(owner)) {
    return false;
  }

  const value = owner as Record<string, unknown>;
  return (
    Object.keys(value).length === 6 &&
    [
      "customerAccountId",
      "providerId",
      "providerRevision",
      "snapshotId",
      "snapshotRevision",
      "displayRevision",
    ].every((key) => isIdentifier(value[key]))
  );
}

export function isCustomerFavouriteActionRequest(
  value: unknown,
): value is CustomerFavouriteActionRequest {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const request = value as Record<string, unknown>;
  return (
    Object.keys(request).length === 4 &&
    typeof request.operationId === "string" &&
    CUSTOMER_FAVOURITE_OPERATION_ID_PATTERN.test(request.operationId) &&
    isOwner(request.owner) &&
    isIdentifier(request.locationId) &&
    (request.intent === "add" || request.intent === "remove")
  );
}

function identity(
  request: CustomerFavouriteActionRequest,
  provider: Pick<
    CustomerFavouriteActionProvider,
    "providerId" | "providerRevision"
  >,
): CustomerFavouriteActionResultIdentity {
  return {
    operationId: request.operationId,
    customerAccountId: request.owner.customerAccountId,
    providerId: provider.providerId,
    providerRevision: provider.providerRevision,
    snapshotId: request.owner.snapshotId,
    snapshotRevision: request.owner.snapshotRevision,
    displayRevision: request.owner.displayRevision,
    locationId: request.locationId,
    intent: request.intent,
  };
}

function createFailure(
  request: CustomerFavouriteActionRequest,
  provider: Pick<
    CustomerFavouriteActionProvider,
    "providerId" | "providerRevision"
  >,
  reason: "invalid-request" | "provider-request-failed",
): CustomerFavouriteActionResult {
  return reason === "invalid-request"
    ? Object.freeze({
        ...identity(request, provider),
        status: "failed" as const,
        reason,
        retryable: false as const,
      })
    : Object.freeze({
        ...identity(request, provider),
        status: "failed" as const,
        reason,
        retryable: true as const,
      });
}

function waitForFixtureDelay(delayMs: number, signal?: AbortSignal) {
  if (signal?.aborted) {
    return Promise.reject(new DOMException("Aborted", "AbortError"));
  }

  if (delayMs <= 0) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, delayMs);
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };

    signal?.addEventListener("abort", abort, { once: true });
  });
}

export function createFrontendFixtureCustomerFavouriteActionProvider({
  // Normal UI feedback has no simulated network wait; slow fixtures opt in.
  delayMs = 0,
  scenario = "preview-authorized",
}: {
  delayMs?: number;
  scenario?: CustomerFavouriteActionFixtureScenario;
} = {}): CustomerFavouriteActionProvider {
  if (
    !(CUSTOMER_FAVOURITE_ACTION_FIXTURE_SCENARIOS as readonly string[]).includes(
      scenario,
    )
  ) {
    throw new TypeError("Unknown Customer Favourite action fixture scenario.");
  }

  const provider: CustomerFavouriteActionProvider = Object.freeze({
    providerId: CUSTOMER_FAVOURITES_FIXTURE_PROVIDER_ID,
    providerRevision: CUSTOMER_FAVOURITES_FIXTURE_PROVIDER_REVISION,
    async mutate(
      request: CustomerFavouriteActionRequest,
      signal?: AbortSignal,
    ) {
      if (!isCustomerFavouriteActionRequest(request)) {
        throw new TypeError("Invalid Customer Favourite action request.");
      }

      if (
        request.owner.providerId !== provider.providerId ||
        request.owner.providerRevision !== provider.providerRevision
      ) {
        return createFailure(request, provider, "invalid-request");
      }

      await waitForFixtureDelay(delayMs, signal);

      const resultIdentity = identity(request, provider);
      if (
        (scenario === "add-failed" && request.intent === "add") ||
        (scenario === "remove-failed" && request.intent === "remove")
      ) {
        return Object.freeze({
          ...resultIdentity,
          status: "failed" as const,
          reason: "provider-request-failed" as const,
          retryable: true,
        });
      }

      switch (scenario) {
        case "unknown":
          return Object.freeze({
            ...resultIdentity,
            status: "unknown" as const,
            reason: "provider-outcome-unknown" as const,
          });
        case "unsupported":
          return Object.freeze({
            ...resultIdentity,
            status: "unsupported" as const,
            reason: "provider-disabled" as const,
          });
        case "auth-expired":
          return Object.freeze({
            ...resultIdentity,
            status: "auth-expired" as const,
            reason: "customer-session-expired" as const,
          });
        case "customer-mismatch":
          return Object.freeze({
            ...resultIdentity,
            status: "customer-mismatch" as const,
            expectedCustomerAccountId: "fixture-customer-other",
            receivedCustomerAccountId: request.owner.customerAccountId,
          });
        default:
          return Object.freeze({
            ...resultIdentity,
            status: "preview-authorized" as const,
            evidence: "frontend-fixture-preview" as const,
            backendSynchronization: "not-claimed" as const,
          });
      }
    },
  });

  return provider;
}

export const frontendFixtureCustomerFavouriteActionProvider =
  createFrontendFixtureCustomerFavouriteActionProvider();

function hasExactIdentity(
  value: Record<string, unknown>,
  expected: CustomerFavouriteActionResultIdentity,
) {
  return Object.entries(expected).every(([key, expectedValue]) =>
    Object.is(value[key], expectedValue),
  );
}

/**
 * Detaches an untrusted provider result before semantic validation.
 *
 * Provider results are deliberately flat data records. Rejecting accessors,
 * symbols, non-enumerable properties, custom prototypes, and values that the
 * structured-clone algorithm cannot copy prevents validation from observing
 * one set of hostile values and the caller from later receiving another.
 */
function detachCustomerFavouriteActionResult(
  value: unknown,
): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  try {
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      return null;
    }

    const descriptors = Object.getOwnPropertyDescriptors(value);
    const ownKeys = Reflect.ownKeys(descriptors);
    if (
      ownKeys.some((key) => {
        if (typeof key !== "string") {
          return true;
        }

        const descriptor = descriptors[key];
        return !descriptor || !descriptor.enumerable || !("value" in descriptor);
      })
    ) {
      return null;
    }

    const detached = structuredClone(value);
    if (
      !detached ||
      typeof detached !== "object" ||
      Array.isArray(detached)
    ) {
      return null;
    }

    return detached as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function validateCustomerFavouriteActionResult(
  value: unknown,
  request: CustomerFavouriteActionRequest,
  provider: Pick<
    CustomerFavouriteActionProvider,
    "providerId" | "providerRevision"
  >,
): CustomerFavouriteActionResult | null {
  if (!isCustomerFavouriteActionRequest(request)) {
    return null;
  }

  const result = detachCustomerFavouriteActionResult(value);
  if (!result) {
    return null;
  }

  const expectedIdentity = identity(request, provider);
  if (!hasExactIdentity(result, expectedIdentity)) {
    return null;
  }

  const identityKeyCount = 9;
  switch (result.status) {
    case "preview-authorized":
      return Object.keys(result).length === identityKeyCount + 3 &&
        result.evidence === "frontend-fixture-preview" &&
        result.backendSynchronization === "not-claimed"
        ? Object.freeze({
            ...expectedIdentity,
            status: "preview-authorized" as const,
            evidence: "frontend-fixture-preview" as const,
            backendSynchronization: "not-claimed" as const,
          })
        : null;
    case "unsupported":
      return Object.keys(result).length === identityKeyCount + 2 &&
        result.reason === "provider-disabled"
        ? Object.freeze({
            ...expectedIdentity,
            status: "unsupported" as const,
            reason: "provider-disabled" as const,
          })
        : null;
    case "failed": {
      if (Object.keys(result).length !== identityKeyCount + 3) {
        return null;
      }

      if (result.reason === "invalid-request" && result.retryable === false) {
        return Object.freeze({
          ...expectedIdentity,
          status: "failed" as const,
          reason: "invalid-request" as const,
          retryable: false as const,
        });
      }

      return result.reason === "provider-request-failed" &&
        result.retryable === true
        ? Object.freeze({
            ...expectedIdentity,
            status: "failed" as const,
            reason: "provider-request-failed" as const,
            retryable: true as const,
          })
        : null;
    }
    case "unknown":
      return Object.keys(result).length === identityKeyCount + 2 &&
        result.reason === "provider-outcome-unknown"
        ? Object.freeze({
            ...expectedIdentity,
            status: "unknown" as const,
            reason: "provider-outcome-unknown" as const,
          })
        : null;
    case "auth-expired":
      return Object.keys(result).length === identityKeyCount + 2 &&
        result.reason === "customer-session-expired"
        ? Object.freeze({
            ...expectedIdentity,
            status: "auth-expired" as const,
            reason: "customer-session-expired" as const,
          })
        : null;
    case "customer-mismatch": {
      const expectedCustomerAccountId = result.expectedCustomerAccountId;
      return Object.keys(result).length === identityKeyCount + 3 &&
        result.receivedCustomerAccountId === request.owner.customerAccountId &&
        isIdentifier(expectedCustomerAccountId) &&
        expectedCustomerAccountId !== result.receivedCustomerAccountId
        ? Object.freeze({
            ...expectedIdentity,
            status: "customer-mismatch" as const,
            expectedCustomerAccountId,
            receivedCustomerAccountId: request.owner.customerAccountId,
          })
        : null;
    }
    default:
      return null;
  }
}
